/**
 * Edit Article Page - REDESIGNED
 * Layout: Title input → Type selector → Cover photo → Body → Gallery → Collaborators
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import ImageUploader from '../components/ImageUploader';
import MultiImageUploader from '../components/MultiImageUploader';
import Navbar from '../components/Navbar';
import '../styles/colors.css';

const ARTICLE_TYPES = ['show review', 'scene update', 'music review', 'interview', 'opinion', 'creative', 'release', 'political', 'commentary', 'other'];

const EditArticlePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    description: '',
    body: '',
    article_type: [],
    city: '',
    state: '',
    cover_photo_url: '',
    image_gallery_urls: []
  });

  const [collaborators, setCollaborators] = useState([]);
  const [originalCollaborators, setOriginalCollaborators] = useState([]);
  const [showAddCollab, setShowAddCollab] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchArticleAndCollaborators();
  }, [id]);

  useEffect(() => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const response = await apiClient.get(`/search/users?q=${encodeURIComponent(searchQuery)}`);
        if (response.data.success) {
          setSearchResults(response.data.users);
        }
      } catch (err) {
        console.error('Search failed:', err);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchArticleAndCollaborators = async () => {
    try {
      const articleResponse = await apiClient.get(`/articles/${id}`);
      if (articleResponse.data.success) {
        const article = articleResponse.data.article;

        if (article.created_by !== user.id) {
          setError('You are not authorized to edit this article');
          setLoading(false);
          return;
        }

        setFormData({
          description: article.description || '',
          body: article.body || '',
          article_type: article.article_type || [],
          city: article.city || '',
          state: article.state || '',
          cover_photo_url: article.cover_photo_url || '',
          image_gallery_urls: article.image_gallery_urls?.filter(Boolean) || []
        });
      }

      const collabResponse = await apiClient.get(`/articles/${id}/collaborators`);
      if (collabResponse.data.success) {
        setCollaborators(collabResponse.data.collaborators);
        setOriginalCollaborators(JSON.parse(JSON.stringify(collabResponse.data.collaborators)));
      }
    } catch (err) {
      setError('Failed to load article');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleTypeToggle = (type) => {
    setFormData(prev => ({
      ...prev,
      article_type: prev.article_type.includes(type)
        ? prev.article_type.filter(t => t !== type)
        : [...prev.article_type, type]
    }));
  };

  const handleAddCollaborator = (result) => {
    const newCollab = {
      collaborator_id: result.id,
      collaborator_name: result.display_name,
      collaborator_role: result.account_role,
      collaboration_status: 'pending',
      isNew: true
    };
    setCollaborators(prev => [...prev, newCollab]);
    setSearchQuery('');
    setSearchResults([]);
    setShowAddCollab(false);
  };

  const handleRemoveCollaborator = (index) => {
    setCollaborators(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      console.log('Submitting update:', formData);
      const response = await apiClient.put(`/articles/${id}`, formData);
      console.log('Update response:', response.data);

      if (response.data.success) {
        // Add new collaborators
        for (const collab of collaborators) {
          if (collab.isNew) {
            try {
              await apiClient.post(`/articles/${id}/collaborators`, {
                collaborator_name: collab.collaborator_name,
                collaborator_role: collab.collaborator_role,
                collaborator_id: collab.collaborator_id || null
              });
              console.log('Added collaborator:', collab.collaborator_name);
            } catch (err) {
              console.error('Failed to add collaborator:', err);
            }
          }
        }

        // Remove deleted collaborators
        for (const originalCollab of originalCollaborators) {
          const stillExists = collaborators.some(c => c.id === originalCollab.id);
          if (!stillExists && originalCollab.id) {
            try {
              await apiClient.delete(`/articles/${id}/collaborators/${originalCollab.id}`);
              console.log('Removed collaborator:', originalCollab.collaborator_name);
            } catch (err) {
              console.error('Failed to remove collaborator:', err);
            }
          }
        }

        alert('Article updated successfully!');
        navigate(`/articles/${id}`);
      }
    } catch (err) {
      console.error('Save error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to update article');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div style={{ minHeight: '100vh', background: 'var(--bg-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-light)' }}>
          Loading article...
        </div>
      </>
    );
  }

  if (error && !formData.description) {
    return (
      <>
        <Navbar />
        <div style={{ minHeight: '100vh', background: 'var(--bg-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '20px' }}>
          <p style={{ color: 'var(--accent-danger)', fontSize: '18px' }}>{error}</p>
          <button onClick={() => navigate('/news?tab=articles')} className="btn btn-secondary">Back to Articles</button>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .edit-article-input {
            font-size: 16px !important;
          }

          .edit-article-textarea {
            font-size: 16px !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }

          .edit-article-title-input {
            font-size: 24px !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }

          .edit-article-card {
            max-width: 100% !important;
            box-sizing: border-box !important;
          }

          .edit-article-cover-photo-container {
            max-width: 100% !important;
            overflow: hidden !important;
          }

          .edit-article-location-grid {
            display: flex !important;
            flex-direction: column !important;
            gap: 12px !important;
          }

          .edit-article-type-grid {
            grid-template-columns: 1fr 1fr !important;
          }

          .edit-article-checkbox {
            width: 20px !important;
            height: 20px !important;
          }

          .edit-article-checkbox-label {
            font-size: 16px !important;
          }

          .edit-article-header {
            flex-direction: row !important;
            justify-content: space-between !important;
            align-items: center !important;
            gap: 16px !important;
          }

          .edit-article-header h1 {
            font-size: 24px !important;
            text-align: center !important;
            flex: 1 !important;
          }

          .edit-article-header-spacer {
            width: 80px !important;
          }
        }
      `}</style>
      <Navbar />
      <div style={{ minHeight: '100vh', background: 'var(--bg-dark)', paddingTop: '140px', paddingBottom: '60px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 20px' }}>

          <div className="edit-article-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <button onClick={() => navigate(`/articles/${id}`)} className="btn btn-secondary">Cancel</button>
            <h1 style={{ color: 'var(--text-light)', fontSize: '32px', fontWeight: '700', margin: 0 }}>Edit Article</h1>
            <div className="edit-article-header-spacer" style={{ width: '100px' }}></div>
            
          </div>

          {error && (
            <div style={{ background: 'var(--accent-danger)', color: 'var(--text-light)', padding: '16px', borderRadius: '8px', marginBottom: '24px' }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            
            {/* Title Input */}
            <div style={{ marginBottom: '24px' }}>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                className="edit-article-title-input" placeholder="Article Title"
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: '32px',
                  fontWeight: '700',
                  background: 'var(--bg-card)',
                  color: 'var(--text-dark)',
                  border: '2px solid var(--border-light)',
                  borderRadius: '8px',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--accent-primary)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--border-light)'}
              />
            </div>

            {/* Article Type Checkboxes */}
            <div className="edit-article-card" style={{ background: 'var(--bg-card)', borderRadius: '12px', padding: '32px', marginBottom: '32px', boxShadow: 'var(--shadow-card)' }}>
              <label style={{ display: 'block', marginBottom: '16px', fontSize: '16px', fontWeight: '700', color: 'var(--text-dark)' }}>
                Article Type (Select all that apply)
              </label>
              <div className="edit-article-type-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                {ARTICLE_TYPES.map(type => (
                  <label key={type} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.article_type.includes(type)}
                      onChange={() => handleTypeToggle(type)}
                      className="edit-article-checkbox"
                      style={{ marginRight: '8px', width: '18px', height: '18px' }}
                    />
                    <span className="edit-article-checkbox-label" style={{ fontSize: '14px', color: 'var(--text-dark)', textTransform: 'capitalize' }}>
                      {type.replace(/_/g, ' ')}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Cover Photo Upload */}
            <div className="edit-article-card edit-article-cover-photo-container" style={{ background: 'var(--bg-card)', borderRadius: '12px', padding: '32px', marginBottom: '32px', boxShadow: 'var(--shadow-card)', textAlign: 'center' }}>
              <label style={{ display: 'block', marginBottom: '16px', fontSize: '16px', fontWeight: '700', color: 'var(--text-dark)' }}>
                Cover Photo
              </label>
              <ImageUploader
                currentImageUrl={formData.cover_photo_url}
                onImageUploaded={(url) => setFormData({ ...formData, cover_photo_url: url })}
                folder="articles"
                previewSize={400}
              />
            </div>

            {/* Body Text */}
            <div className="edit-article-card" style={{ background: 'var(--bg-card)', borderRadius: '12px', padding: '32px', marginBottom: '32px', boxShadow: 'var(--shadow-card)' }}>
              <label style={{ display: 'block', marginBottom: '12px', fontSize: '16px', fontWeight: '700', color: 'var(--text-dark)' }}>
                Article Body *
              </label>
              <textarea
                name="body"
                value={formData.body}
                onChange={handleChange}
                required
                rows="15"
                placeholder="Write your article here..."
                className="edit-article-textarea"
                style={{
                  width: '100%',
                  padding: '16px',
                  border: '1px solid var(--border-light)',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  lineHeight: '1.7'
                }}
              />
            </div>

            {/* Location (Optional) */}
            <div className="edit-article-card" style={{ background: 'var(--bg-card)', borderRadius: '12px', padding: '32px', marginBottom: '32px', boxShadow: 'var(--shadow-card)' }}>
              <label style={{ display: 'block', marginBottom: '16px', fontSize: '16px', fontWeight: '700', color: 'var(--text-dark)' }}>
                Location (Optional)
              </label>
              <div className="edit-article-location-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="City"
                  className="edit-article-input"
                  style={{ padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                />
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  placeholder="State"
                  className="edit-article-input"
                  style={{ padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                />
              </div>
            </div>

            {/* Gallery Upload */}
            <div className="edit-article-card" style={{ background: 'var(--bg-card)', borderRadius: '12px', padding: '32px', marginBottom: '32px', boxShadow: 'var(--shadow-card)' }}>
              <label style={{ display: 'block', marginBottom: '16px', fontSize: '16px', fontWeight: '700', color: 'var(--text-dark)' }}>
                Image Gallery
              </label>
              <MultiImageUploader
                currentImages={formData.image_gallery_urls}
                onImagesChange={(urls) => setFormData({ ...formData, image_gallery_urls: urls })}
                folder="articles"
                maxImages={10}
              />
            </div>

            {/* Collaborators */}
            <div className="edit-article-card" style={{ background: 'var(--bg-card)', borderRadius: '12px', padding: '32px', marginBottom: '32px', boxShadow: 'var(--shadow-card)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: 'var(--text-dark)' }}>Collaborators</h3>
                <button
                  type="button"
                  onClick={() => setShowAddCollab(!showAddCollab)}
                  style={{ 
                    padding: '8px 16px', 
                    fontSize: '13px', 
                    background: 'var(--accent-primary)', 
                    color: 'var(--text-dark)', 
                    border: 'none', 
                    borderRadius: '6px', 
                    cursor: 'pointer', 
                    fontWeight: '700'
                  }}
                >
                  + Add Collaborator
                </button>
              </div>

              {collaborators.length > 0 && (
                <div style={{ marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {collaborators.map((collab, i) => (
                    <div 
                      key={`collab-${collab.id || i}`} 
                      style={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 12px',
                        background: 'var(--accent-primary)',
                        color: 'var(--text-dark)',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                    >
                      <span>{collab.collaborator_name}</span>
                      <button 
                        type="button" 
                        onClick={() => handleRemoveCollaborator(i)} 
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          color: 'var(--accent-danger)', 
                          cursor: 'pointer', 
                          fontSize: '16px', 
                          fontWeight: '700',
                          padding: '0',
                          lineHeight: '1'
                        }}
                        title="Remove"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {showAddCollab && (
                <div style={{ padding: '16px', background: 'var(--bg-card)', border: '1px solid var(--border-light)', borderRadius: '8px' }}>
                  <input
                    type="text"
                    placeholder="Search for collaborator..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ 
                      width: '100%', 
                      padding: '12px', 
                      border: '1px solid var(--border-light)', 
                      borderRadius: '6px', 
                      marginBottom: '8px',
                      fontSize: '14px'
                    }}
                  />
                  {searchResults.length > 0 && (
                    <div style={{ maxHeight: '200px', overflow: 'auto', border: '1px solid var(--border-light)', borderRadius: '6px', background: 'white' }}>
                      {searchResults.map(result => (
                        <div
                          key={result.id}
                          onClick={() => handleAddCollaborator(result)}
                          style={{ 
                            padding: '12px', 
                            cursor: 'pointer', 
                            borderBottom: '1px solid var(--border-light)', 
                            color: 'var(--text-dark)',
                            fontSize: '14px',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = 'var(--smoke-gray)';
                            e.target.style.color = 'var(--accent-primary)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'white';
                            e.target.style.color = 'var(--text-dark)';
                          }}
                        >
                          <strong>{result.display_name}</strong>
                          <span style={{ marginLeft: '8px', fontSize: '13px', opacity: 0.7 }}>
                            ({result.city}, {result.state})
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={saving}
              className="btn btn-primary"
              style={{ width: '100%', padding: '18px', fontSize: '18px', fontWeight: '700' }}
            >
              {saving ? 'Saving...' : 'Update Article'}
            </button>

          </form>
        </div>
      </div>
    </>
  );
};

export default EditArticlePage;
